import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  Loader2, 
  Eye, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  FileText,
  Building2,
  Scan,
  ChevronLeft,
  ChevronRight,
  Download,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMonthlySubmissionsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSubmissions();
  }, [token, statusFilter]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/monthly-submissions?${params}`, { headers });
      setSubmissions(response.data?.items || []);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setOcrResults(submission.ocr_data || null);
    setDetailOpen(true);
  };

  const handleRunOCR = async (documentType) => {
    if (!selectedSubmission) return;
    
    try {
      setOcrProcessing(true);
      const response = await axios.post(
        `${API_URL}/api/admin/ocr/analyze-document`,
        {
          submission_id: selectedSubmission.id,
          document_type: documentType
        },
        { headers }
      );
      
      setOcrResults(response.data.ocr_data);
      toast.success('OCR analysis complete');
      fetchSubmissions();
    } catch (err) {
      console.error('Error running OCR:', err);
      toast.error(err.response?.data?.detail || 'Failed to run OCR analysis');
    } finally {
      setOcrProcessing(false);
    }
  };

  const getMonthName = (month) => {
    return new Date(2024, month - 1).toLocaleString('en-ZA', { month: 'long' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      complete: 'bg-green-100 text-green-800',
      partial: 'bg-amber-100 text-amber-800',
      pending: 'bg-gray-100 text-gray-600',
      reviewed: 'bg-blue-100 text-blue-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'partial': return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'reviewed': return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (!searchQuery) return true;
    return (
      sub.client_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Summary stats
  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const partialCount = submissions.filter(s => s.status === 'partial').length;
  const completeCount = submissions.filter(s => s.status === 'complete').length;

  return (
    <div className="space-y-6" data-testid="admin-monthly-submissions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Monthly Document Submissions</h1>
          <p className="text-[#6C757D]">Review client management accounts and bank statements</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Total Submissions</p>
                <p className="text-2xl font-bold text-[#212529]">{submissions.length}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Pending</p>
                <p className="text-2xl font-bold text-gray-600">{pendingCount}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Partial</p>
                <p className="text-2xl font-bold text-amber-600">{partialCount}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Complete</p>
                <p className="text-2xl font-bold text-green-600">{completeCount}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by client ID or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-submissions"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-12 text-[#6C757D]">
              No submissions found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-center">Mgmt Accounts</TableHead>
                    <TableHead className="text-center">Bank Statements</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">OCR</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubmissions.map((submission) => (
                    <TableRow key={submission.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{submission.company_name || submission.client_id}</p>
                          <p className="text-xs text-[#6C757D] font-mono">{submission.client_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {getMonthName(submission.period_month)} {submission.period_year}
                      </TableCell>
                      <TableCell className="text-center">
                        {submission.management_accounts_uploaded ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {submission.bank_statements_uploaded ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getStatusBadge(submission.status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(submission.status)}
                            {submission.status}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {submission.ocr_processed ? (
                          <Badge className="bg-purple-100 text-purple-800">
                            <Scan className="h-3 w-3 mr-1" />
                            Processed
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">Not run</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewSubmission(submission)}
                          data-testid={`view-submission-${submission.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submission Detail Dialog with OCR */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedSubmission?.company_name || selectedSubmission?.client_id}
              <span className="text-[#6C757D] font-normal ml-2">
                - {selectedSubmission && getMonthName(selectedSubmission.period_month)} {selectedSubmission?.period_year}
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedSubmission && (
            <Tabs defaultValue="documents" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="ocr">OCR Analysis</TabsTrigger>
              </TabsList>

              {/* Documents Tab */}
              <TabsContent value="documents" className="space-y-4 pt-4">
                {/* Management Accounts */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Management Accounts
                      </span>
                      {selectedSubmission.management_accounts_uploaded ? (
                        <Badge className="bg-green-100 text-green-800">Uploaded</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Missing</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  {selectedSubmission.management_accounts_uploaded && (
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#0D3B66]" />
                          <div>
                            <p className="font-medium text-sm">management_accounts.pdf</p>
                            <p className="text-xs text-[#6C757D]">
                              Uploaded on {new Date(selectedSubmission.updated_at).toLocaleDateString('en-ZA')}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRunOCR('management_accounts')}
                            disabled={ocrProcessing}
                          >
                            {ocrProcessing ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Scan className="h-4 w-4 mr-1" />
                            )}
                            Run OCR
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Bank Statements */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Bank Statements
                      </span>
                      {selectedSubmission.bank_statements_uploaded ? (
                        <Badge className="bg-green-100 text-green-800">Uploaded</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Missing</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  {selectedSubmission.bank_statements_uploaded && (
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#0D3B66]" />
                          <div>
                            <p className="font-medium text-sm">bank_statements.pdf</p>
                            <p className="text-xs text-[#6C757D]">
                              Uploaded on {new Date(selectedSubmission.updated_at).toLocaleDateString('en-ZA')}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRunOCR('bank_statements')}
                            disabled={ocrProcessing}
                          >
                            {ocrProcessing ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Scan className="h-4 w-4 mr-1" />
                            )}
                            Run OCR
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </TabsContent>

              {/* OCR Tab */}
              <TabsContent value="ocr" className="space-y-4 pt-4">
                {ocrProcessing ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin text-purple-600 mb-4" />
                    <p className="text-[#6C757D]">Processing document with AI...</p>
                    <p className="text-sm text-[#6C757D] mt-1">This may take a moment</p>
                  </div>
                ) : ocrResults ? (
                  <div className="space-y-4">
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <Scan className="h-6 w-6 text-purple-600 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-purple-900">AI Analysis Complete</p>
                            <p className="text-sm text-purple-700 mt-1">
                              The following data was extracted from the uploaded documents.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Extracted Data Display */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Extracted Financial Data</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {ocrResults.revenue && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-[#6C757D]">Revenue</p>
                              <p className="text-lg font-bold text-[#212529]">
                                R {ocrResults.revenue?.toLocaleString() || 'N/A'}
                              </p>
                            </div>
                          )}
                          {ocrResults.expenses && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-[#6C757D]">Expenses</p>
                              <p className="text-lg font-bold text-[#212529]">
                                R {ocrResults.expenses?.toLocaleString() || 'N/A'}
                              </p>
                            </div>
                          )}
                          {ocrResults.net_profit && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-[#6C757D]">Net Profit</p>
                              <p className="text-lg font-bold text-green-600">
                                R {ocrResults.net_profit?.toLocaleString() || 'N/A'}
                              </p>
                            </div>
                          )}
                          {ocrResults.bank_balance && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-[#6C757D]">Bank Balance</p>
                              <p className="text-lg font-bold text-[#212529]">
                                R {ocrResults.bank_balance?.toLocaleString() || 'N/A'}
                              </p>
                            </div>
                          )}
                          {ocrResults.period && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-[#6C757D]">Period</p>
                              <p className="text-lg font-bold text-[#212529]">
                                {ocrResults.period || 'N/A'}
                              </p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Raw OCR Data */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Raw OCR Output</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                          {JSON.stringify(ocrResults, null, 2)}
                        </pre>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Scan className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-[#6C757D] mb-2">No OCR analysis has been run yet</p>
                    <p className="text-sm text-[#6C757D] mb-6">
                      Go to the Documents tab and click "Run OCR" on an uploaded document to extract data.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMonthlySubmissionsPage;
